/* eslint-disable react/jsx-key */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Calendar, Truck, DollarSign, Users, Video, FileText, Bell, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => iso ? new Date(iso).toLocaleString('fr-CA') : '—';
const money = (n) => n != null ? new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n) : '—';

const TABS = [
  { id: 'reservations', label: 'Réservations', icon: DollarSign },
  { id: 'appointments', label: 'Rendez-vous', icon: Calendar },
  { id: 'walkarounds', label: 'Visites vidéo', icon: Video },
  { id: 'financing', label: 'Financement', icon: FileText },
  { id: 'tradeins', label: 'Reprises', icon: DollarSign },
  { id: 'referrals', label: 'Références', icon: Users },
  { id: 'priceAlerts', label: 'Alertes prix', icon: Bell },
  { id: 'security', label: 'Sécurité', icon: Shield },
];

export default function AdminCarvana() {
  const [tab, setTab] = useState('reservations');
  const [data, setData] = useState({});
  const [counts, setCounts] = useState({});

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    axios.get(`${API}/admin/notifications/counts`, auth()).then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const endpoints = {
      reservations: `${API}/admin/reservations`,
      appointments: `${API}/admin/appointments/all`,
      deliveries: `${API}/admin/deliveries`,
      walkarounds: `${API}/admin/video-walkarounds`,
      financing: `${API}/admin/financing-applications`,
      tradeins: `${API}/admin/tradein-offers`,
      referrals: `${API}/admin/referrals`,
      priceAlerts: `${API}/admin/price-alerts`,
      security: `${API}/admin/security/summary`,
    };
    axios.get(endpoints[tab], auth()).then((r) => setData((d) => ({ ...d, [tab]: r.data }))).catch(() => {});
  }, [tab]);

  const refund = async (id) => {
    if (!window.confirm('Rembourser cette réservation?')) return;
    try {
      await axios.post(`${API}/admin/reservations/${id}/refund`, {}, auth());
      const r = await axios.get(`${API}/admin/reservations`, auth());
      setData({ ...data, reservations: r.data });
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.detail || e.message));
    }
  };

  const current = data[tab];

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-black text-slate-900">Carvana — Opérations</h1>
            <p className="text-slate-500">Réservations, reprises et concierge en un seul écran.</p>
          </div>
          {counts.total > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg flex items-center gap-2" data-testid="notification-badge">
              <Bell className="h-4 w-4" />
              <span className="font-bold">{counts.total}</span>
              <span className="text-xs">nouveaux (24h)</span>
            </div>
          )}
        </div>

        <div className="flex gap-2 flex-wrap border-b border-slate-200 mb-6 overflow-x-auto">
          {TABS.map((tt) => {
            const Icon = tt.icon;
            return (
              <button
                key={tt.id}
                onClick={() => setTab(tt.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 ${tab === tt.id ? 'border-[#3b82f6] text-[#3b82f6]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                data-testid={`tab-${tt.id}`}
              >
                <Icon className="h-4 w-4" />
                {tt.label}
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {tab === 'reservations' && (
            <Table
              headers={['Client', 'Véhicule', 'Montant', 'Statut', 'Créée', 'Actions']}
              rows={(current?.reservations || []).map((r) => [
                <div>{r.customer_name}<br /><span className="text-xs text-slate-500">{r.customer_email}</span></div>,
                <div>{r.vehicle_snapshot?.year} {r.vehicle_snapshot?.make} {r.vehicle_snapshot?.model}</div>,
                money(r.amount_cad),
                <StatusBadge status={r.status} />,
                fmt(r.created_at),
                r.status === 'confirmed' && <button onClick={() => refund(r.id)} className="text-red-600 text-xs font-bold" data-testid={`refund-${r.id}`}>Rembourser</button>,
              ])}
            />
          )}
          {tab === 'appointments' && (
            <Table
              headers={['Type', 'Client', 'Véhicule', 'Statut', 'Quand']}
              rows={(current?.appointments || []).map((a) => [
                <span className="capitalize font-semibold">{a.kind}</span>,
                <div>{a.customer_name}<br /><span className="text-xs text-slate-500">{a.customer_email}</span></div>,
                a.vehicle_id || '—',
                <StatusBadge status={a.status} />,
                fmt(a.when),
              ])}
            />
          )}
          {tab === 'deliveries' && (
            <Table
              headers={['Client', 'Adresse', 'Frais', 'Date préférée', 'Statut']}
              rows={(current?.deliveries || []).map((d) => [
                <div>{d.customer_name}<br /><span className="text-xs text-slate-500">{d.customer_email}</span></div>,
                <div>{d.address?.street}<br />{d.address?.city}, {d.address?.province} {d.address?.postal_code}</div>,
                money(d.fee_cad),
                d.preferred_date || '—',
                <StatusBadge status={d.status} />,
              ])}
            />
          )}
          {tab === 'walkarounds' && (
            <Table
              headers={['Client', 'Véhicule', 'Plateforme', 'Date', 'Statut']}
              rows={(current?.bookings || []).map((w) => [
                <div>{w.customer_name}<br /><span className="text-xs text-slate-500">{w.customer_email}</span></div>,
                w.vehicle_id,
                w.preferred_platform,
                fmt(w.preferred_date),
                <StatusBadge status={w.status} />,
              ])}
            />
          )}
          {tab === 'financing' && (
            <Table
              headers={['Nom', 'Courriel', 'Revenu/mois', 'Emploi', 'Terme', 'Créée']}
              rows={(current?.applications || []).map((f) => [
                `${f.first_name} ${f.last_name}`,
                f.email,
                money(f.monthly_income),
                f.employment_status,
                `${f.desired_term_months} mois`,
                fmt(f.created_at),
              ])}
            />
          )}
          {tab === 'tradeins' && (
            <Table
              headers={['Client', 'Véhicule', 'Km', 'Cond.', 'Estimation', 'Créée']}
              rows={(current?.offers || []).map((o) => [
                <div>{o.customer_name}<br /><span className="text-xs text-slate-500">{o.customer_email}</span></div>,
                `${o.year} ${o.make} ${o.model}`,
                o.mileage,
                o.condition,
                `${money(o.estimate?.estimate_low_cad)} – ${money(o.estimate?.estimate_high_cad)}`,
                fmt(o.created_at),
              ])}
            />
          )}
          {tab === 'referrals' && (
            <Table
              headers={['Référent', 'Ami', 'Récompense', 'Statut', 'Créée']}
              rows={(current?.referrals || []).map((r) => [
                <div>{r.referrer_name}<br /><span className="text-xs text-slate-500">{r.referrer_email}</span></div>,
                <div>{r.friend_name}<br /><span className="text-xs text-slate-500">{r.friend_email}</span></div>,
                money(r.reward_amount_cad),
                <StatusBadge status={r.status} />,
                fmt(r.created_at),
              ])}
            />
          )}
          {tab === 'priceAlerts' && (
            <Table
              headers={['Client', 'Véhicule', 'Prix initial', 'Notifiés', 'Créée']}
              rows={(current?.alerts || []).map((a) => [
                <div>{a.customer_name}<br /><span className="text-xs text-slate-500">{a.customer_email}</span><br /><span className="text-xs text-slate-500">{a.customer_phone}</span></div>,
                <div>{a.vehicle_snapshot?.year} {a.vehicle_snapshot?.make} {a.vehicle_snapshot?.model}</div>,
                money(a.starting_price),
                a.notified_count || 0,
                fmt(a.created_at),
              ])}
            />
          )}
          {tab === 'security' && current && (
            <div className="p-6 space-y-6" data-testid="security-tab">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-xs uppercase text-slate-500 font-bold">Blocages (24h)</p>
                  <p className="text-3xl font-black text-red-600 mt-1">{current.total_blocks_24h || 0}</p>
                </div>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <p className="text-xs uppercase text-slate-500 font-bold">IP uniques suspectes</p>
                  <p className="text-3xl font-black text-slate-800 mt-1">{(current.top_ips || []).length}</p>
                </div>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <p className="text-xs uppercase text-slate-500 font-bold">User-Agents bloqués</p>
                  <p className="text-3xl font-black text-slate-800 mt-1">{(current.top_uas || []).length}</p>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-slate-900 mb-2">Top IPs suspectes (24h)</h3>
                <Table
                  headers={['IP', 'Blocages', 'Types', 'Dernier UA', 'Vu']}
                  rows={(current.top_ips || []).map((r) => [
                    <span className="font-mono text-xs">{r.ip}</span>,
                    <span className="font-bold">{r.count}</span>,
                    (r.kinds || []).join(', '),
                    <span className="text-xs text-slate-500 truncate max-w-[240px] inline-block">{(r.sample_ua || '').slice(0, 60)}</span>,
                    fmt(r.last_seen),
                  ])}
                />
              </div>

              <div>
                <h3 className="font-bold text-slate-900 mb-2">Top User-Agents bloqués (24h)</h3>
                <Table
                  headers={['User-Agent', 'Blocages', 'Vu']}
                  rows={(current.top_uas || []).map((r) => [
                    <span className="font-mono text-xs">{(r.ua || '').slice(0, 100)}</span>,
                    <span className="font-bold">{r.count}</span>,
                    fmt(r.last_seen),
                  ])}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function Table({ headers, rows }) {
  if (!rows || rows.length === 0) {
    return <div className="p-8 text-center text-slate-400">Aucune donnée.</div>;
  }
  return (
    <table className="min-w-full text-sm">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          {headers.map((h, i) => <th key={i} className="text-left px-4 py-3 font-semibold text-slate-700">{h}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <tr key={i} className="hover:bg-slate-50">
            {r.map((c, j) => <td key={j} className="px-4 py-3 text-slate-800 align-top">{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusBadge({ status }) {
  const colors = {
    confirmed: 'bg-emerald-100 text-emerald-700',
    pending:   'bg-yellow-100 text-yellow-700',
    requested: 'bg-blue-100 text-blue-700',
    refunded:  'bg-slate-100 text-slate-500',
    expired:   'bg-red-100 text-red-700',
    failed:    'bg-red-100 text-red-700',
    submitted: 'bg-blue-100 text-blue-700',
    new:       'bg-emerald-100 text-emerald-700',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${colors[status] || 'bg-slate-100 text-slate-600'}`}>{status || '—'}</span>;
}
